const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:']
const EMAIL_PATTERN = /^[^\s@:/]+@[^\s@]+\.[^\s@]+$/

const HOST_WITH_PORT_PATTERN = /^(?:localhost|[^\s:/]+\.[^\s:/]+):\d/

/**
 * Parses a value with the URL API, or returns null when it is not a valid absolute URL.
 */
function parseUrl(value: string): URL | null {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

/**
 * Normalizes the address typed in the link popover into a safe href, or null when it must be refused.
 * An address typed without protocol ("www.example.test") is rebuilt as https.
 */
export function normalizeLinkHref(
  input: string | null | undefined
): string | null {
  const value = (input ?? '').trim()
  if (!value) {
    return null
  }

  // An explicit scheme wins: "mailto:a@b.test" or "https://user@host" must not be read as a bare email.
  const absoluteUrl = parseUrl(value)
  if (absoluteUrl && ALLOWED_PROTOCOLS.includes(absoluteUrl.protocol)) {
    return absoluteUrl.href
  }

  if (EMAIL_PATTERN.test(value)) {
    return parseUrl(`mailto:${value}`)?.href ?? null
  }

  const url = absoluteUrl ? null : parseUrl(`https://${value}`)
  if (url && ALLOWED_PROTOCOLS.includes(url.protocol)) {
    return url.href
  }

  // "localhost:3000" parses as a custom "localhost:" scheme: only a host followed by a port is retried as https.
  if (!value.includes('//') && HOST_WITH_PORT_PATTERN.test(value)) {
    return parseUrl(`https://${value}`)?.href ?? null
  }

  return null
}
